import React, { Component } from 'react';

// Actions
import { setSelectedBenchmarks } from '@/redux/modules/advanced-search';

// Components
import AdvancedSearchResult from './AdvancedSearchResult';
import { withErrorBoundary } from '../errors';

// Libraries
import { connect } from 'react-redux';
import classNames from 'classnames';

// Table Columns Definition
import { getColumnsDefinition, SearchResultColumns } from './columns-definition/SearchResult';

// Types and Enums
import AdvancedSearchItem from '@/types/advanced-search-item';
import ComparisonBenchmark, { ComparisonBenchmarkForDataTable } from '@/types/comparison-benchmark';
import { BenchmarkSearchState } from '@/redux/modules/types/benchmark-search';
import { SearchType } from '@/enums/AdvancedSearch';

// Utils
import { FormattedMessage } from '@/utils/i18n';

// Legacy support
import LegacyBenchmarkSearchResult from './LegacyBenchmarkSearchResult';
import useThemeVersion from '@/hooks/useThemeVersion';

type DispatchProps = {
  setSelectedBenchmarks: (benchmarks: ComparisonBenchmark[]) => void;
};

type Props = DispatchProps & {
  addingToComparison: boolean;
  addingToPortfolio: boolean;
  benchmarkState: BenchmarkSearchState;
  className?: string;
  classicVersionOnly?: boolean;
  columnOverrides?: Record<string, { size?: number; isRightAligned?: boolean }>;
  columnPinning?: { left?: string[]; right?: string[] };
  hasMore: boolean;
  hideSaveFund?: boolean;
  insideModal?: boolean;
  loading?: boolean;
  onAddToPortfolio: () => void;
  onLoadMoreResults: () => void;
  onStartComparison: () => void;
  onToggleSaved: (benchmark: ComparisonBenchmark) => void;
  savedBenchmarks: ComparisonBenchmark[];
  searchQuery?: string;
  showTicker?: boolean;
  tableColumns: SearchResultColumns[];
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
};

class BenchmarkSearchResultV2 extends Component<Props> {
  static defaultProps = {
    onToggleSaved: () => {},
  };

  formatData = (benchmarks?: ComparisonBenchmark[]) => {
    if (!benchmarks) return [];

    return benchmarks.map(benchmark => ({
      ...benchmark,
      names: {
        full: benchmark.name,
      },
    }));
  };

  getActions = () => {
    const { addingToPortfolio, addingToComparison, onAddToPortfolio, onStartComparison } = this.props;
    return [
      {
        loading: addingToPortfolio,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Benchmarks.${addingToPortfolio ? 'CreatingCustom' : 'CreateCustom'}`} />,
        onClick: onAddToPortfolio,
      },
      {
        isLoading: addingToComparison,
        icon: 'plus',
        label: <FormattedMessage id={`Search.Benchmarks.${addingToComparison ? 'AddingToComparison' : 'AddToComparison'}`} />,
        onClick: onStartComparison,
      },
    ];
  };

  handleRowSelectionChange = (upcomingSelectedRows: AdvancedSearchItem[]) => {
    this.props.setSelectedBenchmarks(upcomingSelectedRows as ComparisonBenchmark[]);
  };

  render() {
    const {
      benchmarkState: { selectedBenchmarks, benchmarks, focusedBenchmark },
      benchmarkState,
      className,
      classicVersionOnly,
      columnOverrides,
      columnPinning,
      insideModal,
      loading,
      onToggleSaved,
      totalResultsCount,
      useWindowScrollAncestor,
      hasMore,
      hideSaveFund,
      savedBenchmarks,
      showTicker,
      tableColumns,
    } = this.props;

    const { columns } = getColumnsDefinition<ComparisonBenchmarkForDataTable>({
      itemState: benchmarkState,
      onToggleSaved,
      type: SearchType.BENCHMARKS,
      hideSaveItem: hideSaveFund,
      allowedColumns: tableColumns,
      showTicker,
      columnOverrides,
    });

    return (
      <AdvancedSearchResult
        actions={this.getActions()}
        className={classNames('benchmark-search-result', className)}
        classicVersionOnly={classicVersionOnly}
        columnPinning={columnPinning}
        columns={columns}
        data={benchmarks && this.formatData(benchmarks)}
        focusedItem={focusedBenchmark}
        hasMore={hasMore}
        insideModal={insideModal}
        loading={loading}
        onLoadMoreResults={this.props.onLoadMoreResults}
        onRowSelectionChange={this.handleRowSelectionChange}
        savedItems={savedBenchmarks}
        searchQuery={this.props.searchQuery}
        selectedItems={this.formatData(selectedBenchmarks)}
        selectedTitle="Search.Benchmarks.SelectedTitle"
        totalResultsCount={totalResultsCount}
        useWindowScrollAncestor={useWindowScrollAncestor}
      />
    );
  }
}

const BenchmarkSearchResult = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  return isLightVersion ? <BenchmarkSearchResultV2 {...props} /> : <LegacyBenchmarkSearchResult {...props} />;
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, {
  setSelectedBenchmarks,
})(withErrorBoundary(BenchmarkSearchResult));
